// GCG /admin/shops/{shopId}/telemetry endpoint transport.
//
// Two-stream event channel per DECISION-012 and ASK-002 resolution:
//   • perf telemetry  → Galleria engineering (short-retention, low PII)
//   • business events → Galleria + merchant later (long-retention, attributable)
//   • runtime:error   → GCP Error Reporting via Pino-structured logs (DECISION-012)
//
// Wire shape (GCG, ASK-145 §18.10 — the credential since the G4 authz fix, §17):
//   POST /admin/shops/{shopId}/telemetry
//   Authorization: Bearer <sessionToken>   — the SHOP SESSION token from
//     `POST /api/kapowx/v1/shops/{shopId}/sessions` (201; anonymous works), or its rotation from a
//     cart op's `X-Galleria-Session-Token`. The session row must be live and of THIS shop. A visitor's
//     Firebase token is no longer accepted: dev answered it 403, which silenced the `runtime:error`
//     relay (F-139b).
//   Body: { events: [{ eventType, timestamp, sessionId, payload }, ...] }  (GCG replaces sessionId
//     with the token's `sub`.) Caps: ≤50 events, ≤32 KB total.
//   Response: 204 · 401 bad token · 403 session_shop_mismatch · 410 session_expired · 429 rate_limited
//     (Retry-After) · 404 shop gone.
//
// Behaviour: fire-and-forget. Failures are warned to console but never
// propagated — telemetry must never break the runtime. Nothing is POSTed
// without a session token: a request GCG can only refuse is noise, not a signal.

import { storedShopSession } from "./_shop-session.js";

// One session per shop per page, shared by every transport the emitter builds (it builds one per
// event). The cart's stored session wins when it is this shop's — it is the token "already held".
const minted = new Map();        // shopId → Promise<session | null>
const pausedUntil = new Map();   // shopId → epoch ms (429 Retry-After)

/** Test seam: forget minted sessions and back-offs (a fresh page). */
export function resetTelemetrySessions() { minted.clear(); pausedUntil.clear(); }

export class TelemetryTransport {
	constructor({ baseUrl, shopId, apiNamespace, firebaseIdToken, getFreshIdToken }) {
		this.baseUrl         = String(baseUrl || "").replace(/\/$/, "");
		this.shopId          = shopId;
		this.apiNamespace    = apiNamespace || "kapowx/v1";
		this.firebaseIdToken = firebaseIdToken || null;   // only links a minted session to the customer
		this.getFreshIdToken = getFreshIdToken || null;   // E3
	}

	get _url() {
		return `${this.baseUrl}/admin/shops/${encodeURIComponent(this.shopId)}/telemetry`;
	}

	// The shop session token: the cart's for this shop, else one this page minted, else a new one —
	// `POST …/sessions` exactly as the cart creates it (the Firebase token, when signed in, links it).
	// null when none can be had; the caller then sends nothing.
	async _sessionToken({ fresh = false } = {}) {
		// Once this page has minted (because the held one was refused, or there was none), it keeps to its own.
		if (!fresh && !minted.has(this.shopId)) {
			const held = storedShopSession(this.shopId);
			if (held?.sessionToken) return held.sessionToken;
		}
		if (fresh || !minted.has(this.shopId)) minted.set(this.shopId, this._mint());
		const s = await minted.get(this.shopId);
		if (!s) minted.delete(this.shopId);   // a failed mint is retried by the next event, not cached
		return s?.sessionToken || null;
	}

	async _mint() {
		try {
			const headers = { "Content-Type": "application/json" };
			const tok = (this.getFreshIdToken ? await this.getFreshIdToken().catch(() => null) : null) || this.firebaseIdToken;
			if (tok) headers.Authorization = `Bearer ${tok}`;
			const resp = await fetch(`${this.baseUrl}/api/${this.apiNamespace}/shops/${encodeURIComponent(this.shopId)}/sessions`, { method: "POST", headers, body: "{}" });
			if (!resp.ok) { console.warn(`[telemetry] session create for ${this.shopId} → ${resp.status}; events dropped`); return null; }
			const data = await resp.json();
			return data?.sessionToken ? { sessionToken: data.sessionToken, expiresAt: data.expiresAt } : null;
		} catch (err) {
			console.warn("[telemetry] session create failed:", err.message || err);
			return null;
		}
	}

	_post(events, token) {
		return fetch(this._url, {
			method:  "POST",
			headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
			body:    JSON.stringify({ events }),
		});
	}

	// Sends a batch. Caller controls batching; v1 callers send 1-element
	// arrays for simplicity (matches the Telemetry-0 scope). Promise resolves
	// once the network call is done but errors are swallowed — callers
	// should NOT await this for correctness, only for sequencing tests.
	async send(events) {
		if (!Array.isArray(events) || events.length === 0) return;
		if ((pausedUntil.get(this.shopId) || 0) > Date.now()) return;
		try {
			const token = await this._sessionToken();
			if (!token) return;
			let resp = await this._post(events, token);
			// 401 / 410 / 403 session_shop_mismatch — the token is dead or another shop's: mint this
			// shop's own session and send once more (§18.10's recovery for each).
			if (resp.status === 401 || resp.status === 410 || resp.status === 403) {
				const again = await this._sessionToken({ fresh: true });
				if (!again) return;
				resp = await this._post(events, again);
			}
			if (resp.status === 429) {
				const after = Number(resp.headers?.get?.("Retry-After"));
				pausedUntil.set(this.shopId, Date.now() + (Number.isFinite(after) && after > 0 ? after : 1) * 1000);
			}
			if (!resp.ok) {
				console.warn(`[telemetry] POST ${this._url} → ${resp.status}`);
			}
		} catch (err) {
			console.warn("[telemetry] fetch failed:", err.message || err);
		}
	}
}

// Builds a transport targeting a specific shop. Telemetry events are
// per-shop because the endpoint is, even though events like
// `perf:page-load` aren't intrinsically shop-scoped — pass the active
// shop the customer is in (or the gateway default for events that fire
// before any portal navigation). Returns null when gateway isn't
// configured (dev / preview without GCG).
export function makeTelemetryTransport(appConfig, shopId) {
	const gw = appConfig?.gatewayConfig;
	const resolvedShopId = shopId || gw?.shopId;
	if (!gw?.baseUrl || !resolvedShopId) return null;
	return new TelemetryTransport({
		baseUrl:         gw.baseUrl,
		shopId:          resolvedShopId,
		apiNamespace:    gw.apiNamespace,
		firebaseIdToken: gw.firebaseIdToken,
		getFreshIdToken: gw.getFreshIdToken,   // E3
	});
}
